import {useEffect, useRef} from 'react';

import {useDataEventTracking} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {getIsInManagedTrial} from '../../../store/data/company/companySelectors';
import {getWasOnboardingCompleted} from '../../../store/data/preferences/preferencesSelectors';
import {getIsUserFirstGlobalAdmin} from '../../../store/data/teammates/teammatesSelectors';
import {getSandboxTeam} from '../../../store/data/teams/teamsSelectors';
import {getAvailableSetupGuide} from '../../../store/stateless/onboardingSelectors';
import {useOnboardingModalState} from '../../app/topBar/onboarding/onboardingContext';
import {useOnboardingFlowState} from './onboardingFlowContext';
import {flows, onboardingFlows} from './onboardingFlows';
import {onboardingFlowSteps} from './onboardingFlowSteps';
import {
  OnboardingFlow,
  OnboardingFlowNameEnum,
  OnboardingFlowStepProps,
  OnboardingFlowTransitionEnum,
} from './onboardingFlowTypes';

// This helper should be used for navigating all onboarding flow experiences.
export function useOnboardingFlow() {
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();
  const onboardingModalContext = useOnboardingModalState();

  const {
    onboardingFlow: currentFlowName,
    setOnboardingFlow: setCurrentFlowName,
    onboardingFlowStep: currentStepName,
    setOnboardingFlowStep: setCurrentStepName,
  } = useOnboardingFlowState();

  const onEndOnboardingFlow = () => {
    setCurrentFlowName(undefined);
    setCurrentStepName(undefined);
  };

  const setupGuide = useAppSelector(getAvailableSetupGuide);
  const isUserFirstGlobalAdmin = useAppSelector(getIsUserFirstGlobalAdmin);
  const isInManagedTrial = useAppSelector(getIsInManagedTrial);
  const wasOnboardingCompleted = useAppSelector(getWasOnboardingCompleted);
  const sandboxTeam = useAppSelector(getSandboxTeam);

  const gating = {
    setupGuide,
    isUserFirstGlobalAdmin,
    isInManagedTrial,
    wasOnboardingCompleted,
  };

  const flowProps = {
    gating,
  };

  const stepProps: OnboardingFlowStepProps = {
    dispatch,
    sandboxTeam,
    onboardingModalContext,
    gating,
    trackDataEvent,
    onEndOnboardingFlow,
  };

  const getApplicableOnboardingFlowName = async (): Promise<OnboardingFlowNameEnum | undefined> => {
    for (const flowName of flows) {
      const flow = onboardingFlows[flowName];

      const flowIsApplicable = await flow.isApplicable(flowProps);

      if (flowIsApplicable) {
        return flowName;
      }
    }

    return undefined;
  };

  const getFlow = async (): Promise<OnboardingFlow | undefined> => {
    if (currentFlowName) {
      return onboardingFlows[currentFlowName];
    }

    const applicableFlowName = await getApplicableOnboardingFlowName();

    if (!applicableFlowName) {
      return undefined;
    }

    const applicableFlow = onboardingFlows[applicableFlowName];
    const shouldStartFlow = await applicableFlow.shouldStart(flowProps);

    if (!shouldStartFlow) {
      return undefined;
    }

    setCurrentFlowName(applicableFlowName);

    return applicableFlow;
  };

  return {
    navigateOnboardingFlow: async (transition: OnboardingFlowTransitionEnum): Promise<boolean> => {
      const flow = await getFlow();

      // We're not currently in an onboarding flow.
      if (!flow) {
        return false;
      }

      if (currentStepName) {
        await onboardingFlowSteps[currentStepName]?.onEnd?.(stepProps);
      }

      const nextStepName = await flow.transitions[transition]?.(stepProps);

      // The onboarding flow is completed.
      if (!nextStepName) {
        onEndOnboardingFlow();
        return false;
      }

      const nextStep = onboardingFlowSteps[nextStepName];

      setCurrentStepName(nextStepName);
      await nextStep.onStart(stepProps);

      return true;
    },
  };
}

// Initialize the onboarding flow, automatically showing the first step/popover (when applicable).
export function useStartOnboardingFlow() {
  const didStartOnboardingFlowRef = useRef(false);

  const {navigateOnboardingFlow} = useOnboardingFlow();

  useEffect(() => {
    if (didStartOnboardingFlowRef.current) {
      return;
    }

    didStartOnboardingFlowRef.current = true;

    // Guaranteed to run once for the lifetime of the component.
    navigateOnboardingFlow(OnboardingFlowTransitionEnum.START_ONBOARDING);
  });
}
